import { Component, ElementRef, OnDestroy, OnInit } from "@angular/core"
import { BsModalService } from "ngx-bootstrap/modal"
import { AppState } from "../../core"
import { BuildingDoc, BuildingService, PostProcessingService } from "../../core/services"
import { TableColumn, TableViewComponentBase, TableViewQueryRequest, TableViewQueryResponse } from "../../shared/components/table-view/table-view-component-base"
import { DatePipe } from "@angular/common"
import { CorrelationSummaryRecord } from "../../core/models/correlation-summary-record"
import { CorrelationSummaryByPointRecord } from "../../core/models/correlation-summary-by-point-record"
import { CorrelationSummaryByPathRecord } from "../../core/models/correlation-summary-by-path-record"


@Component({
  selector: 'app-correlation-summary',
  templateUrl: 'correlation-summary.component.html',
  styleUrls: [
    '../../shared/components/table-view/scss/table-view.scss',
    '../../shared/components/table-view/scss/app-tab-full-height.scss',
    'correlation-summary.component.scss'
  ]
})
export class CorrelationSummaryComponent extends TableViewComponentBase<CorrelationSummaryRecord> implements OnInit, OnDestroy {
  
  // define ranges of cells (to set background color and misc css styles)
  readonly colGroups : any[] = [
    { 
      headerCellCssClass: 'summary-col-grp-0',
      index: 0,
      size: 9
    },
    {
      headerCellCssClass: 'summary-col-grp-1',
      dataCellCssClass: 'summary-data-cell-default',
      index: 9,
      size: 5
    },
    {
      headerCellCssClass: 'summary-col-grp-2',
      dataCellCssClass: 'summary-data-cell-default',
      index: 14,
      size: 1
    },
    {
      headerCellCssClass: 'summary-col-grp-3',
      dataCellCssClass: 'summary-data-cell-default',
      index: 15,
      size: 1
    },
    {
      headerCellCssClass: 'summary-col-grp-horizontal',
      dataCellCssClass: 'summary-data-cell-default',
      index: 16,
      size: 11
    },
    {
      headerCellCssClass: 'summary-col-grp-vertical',
      dataCellCssClass: 'summary-data-cell-default',
      index: 27,
      size: 12
    }
  ]
  
  stages : string[] = []
  buildingIds : string[] = []
  participants : string[] = []
  buildingById: any = { }
  buildings: BuildingDoc[] = []

  constructor(
      appState: AppState,
      modalService: BsModalService,
      public postProcessingService: PostProcessingService,
      elementRef: ElementRef,
      datePipe: DatePipe,
      public buildingService: BuildingService
  ){
    super(appState,modalService,elementRef,datePipe)
    this.pageSize = 0 // no paging

    const initColumnDef = (col: any,i:number) => {
      const colGroup = this.colGroups.find(grp=>i>=grp.index&&(i<grp.index+grp.size))
      col.visible = col.visible !== false
      col.headerCellCssClass = [ col.headerCellCssClass, colGroup.headerCellCssClass ].filter(cssName=>cssName).join(' ')
      col.dataCellCssClass = [ col.dataCellCssClass, colGroup.dataCellCssClass ].filter(cssName=>cssName).join(' ')
      if ( !col.template ) {
        col.template = 'numeric'
      }
      return col as TableColumn
    }
    
    this.tableColumns  = [
      { title: 'Stage', name: 'stage', visible: false, template: 'text'},
      { title: 'Participant', name: 'participant', visible: false, template: 'text'},
      { title: 'Grouping', name: 'groupingfield', visible: false, template: 'text'},
      { title: 'Building ID', name: 'buildingid', visible: false, template: 'text'},
      { title: 'Path ID', name: 'pathid', visible: true, template: 'text'},
      { title: 'Point ID', name: 'pointid', visible: false, template: 'text'},
      { title: 'Summary Group', name: 'summgroup', visible: false, template: 'text'},
      { title: 'Summary Row', name: 'summrow', dataCellCssClass: 'font-weight-bold text-left', template: 'summrow'},
      { title: 'Type', name: 'type', visible: true, template: 'text'},

      { title: 'Calls Placed', name: 'callsplaced'},
      { title: 'Completed Calls', name: 'completedcalls'},
      { title: 'Correlated Calls', name: 'correlatedcalls'},
      { title: 'Completed Calls Yield (%)', name: 'completedcallsyield', template: 'percent'},
      { title: 'Correlation Efficiency (%)', name: 'correlatedcallsyield', template: 'percent'},

      { title: 'x/y Per Tech Proportion', name: 'pertechproportion', template: 'percent'},

      { title: 'Average Time to Fix (sec)', name: 'timetofix'},
      
      { title: 'Valid x/y Locations', name: 'horizvalidlocations'},
      { title: 'Location Yield (%)', name: 'horizvalidlocationsyield', template: 'percent'},
      { title: 'Within 50m', name: 'horizwithin50m'},
      { title: 'Within 50m (%)', name: 'horizwithin50mpct', template: 'percent'},
      { title: 'Within 150m', name: 'horizwithin150m'},
      { title: 'Within 150m (%)', name: 'horizwithin150mpct', template: 'percent'},
      { title: '67th Percentile (m)', name: 'horizpercentile67th'},
      { title: '80th Percentile (m)', name: 'horizpercentile80th'},
      { title: '90th Percentile (m)', name: 'horizpercentile90th'},
      { title: 'Average x/y Uncertainty', name: 'horizaverageuncertainty'},
      { title: 'Within uncertainty (%)', name: 'horizwithinuncertaintypct', template: 'percent'},
      
      { title: 'Valid Height', name: 'vertvalidlocations'},
      { title: 'Height Yield', name: 'vertvalidlocationsyield', template: 'percent'},
      { title: 'Average Height Error (m)', name: 'vertaltitudeerroravg'},
      { title: 'Std.Dev. Height Error (m)', name: 'vertaltitudeerrorstddev'},
      { title: 'Average Height Distance Error (m)', name: 'vertverticalerroravg'},
      { title: 'Height Within 3 meters', name: 'vertwithin3m'},
      { title: 'Height Within 3 meters (%)', name: 'vertwithin3mpct', template: 'percent'},
      { title: '% Within 3 meters relative to completed calls', name: 'vertwithin3mpctrtcompletedcalls', template: 'percent'},
      { title: '% Within 3 meters relative to correlated calls', name: 'vertwithin3mpctrtcorrelatedcalls', template: 'percent'},
      { title: '67th Percentile (m)', name: 'vertpercentile67th'},
      { title: '80th Percentile (m)', name: 'vertpercentile80th'},
      { title: '90th Percentile (m)', name: 'vertpercentile90th'}
    ].map((col,index)=> initColumnDef(col,index))
  }


  ngOnInit(): void {
    this.init()
  }


  ngOnDestroy(): void {
    this.destroy()
  }

  init() {
    super.init()
    this.populateColumnsBeforeSummRow()
  }

  columnsBeforeSummRow : TableColumn[] = []
  columnStateChanged(column: TableColumn) {
    super.columnStateChanged(column)
    this.populateColumnsBeforeSummRow()
  }

  populateColumnsBeforeSummRow() {
    const summRowColIndex = this.visibleColumns.findIndex(c=>c.name=='summrow')    
    this.columnsBeforeSummRow = summRowColIndex > 0 ? this.visibleColumns.slice(0,summRowColIndex) : []
  }

  buildingRecs : CorrelationSummaryRecord[] = []
  pointRecs : CorrelationSummaryByPointRecord[] = []
  pathRecs : CorrelationSummaryByPathRecord[] = []

  doQuery = async (queryReq: TableViewQueryRequest) => {    
    this.buildingRecs = []
    this.pointRecs = []

    let buildingQueryError = null

    const pointidFilter = queryReq?.filters.find(f=>f.key=='pointid')
    const pathidFilter = queryReq?.filters.find(f=>f.key=='pathid')

    // building query does not allow pathid and pointid
    const buildingFilters = queryReq?.filters.filter(f=>f.key!='pointid'&&f.key!='pathid')
    let buildingQueryPromise = this.postProcessingService.findCorrelationSummaryRecords(
        {...queryReq,filters:buildingFilters}).then(resp=>{
      this.buildingRecs = resp.rows
    }).catch(error=>{
      this.buildingRecs = []
      buildingQueryError=error;
      this.postProcessingService.publishError(error,this)
    })

    const pathFilters = [...buildingFilters]
    if ( pathidFilter ) {
      pathFilters.push(pathidFilter)
    }
    let pathQueryError = null
    let pathQueryPromise = this.postProcessingService.findCorrelationSummaryByPathRecords(
      {...queryReq,filters:pathFilters}
    ).then(resp=>{
      this.pathRecs = resp.rows
    }).catch(error=>{
      pathQueryError=error;
      this.postProcessingService.publishError(error,this)
    })

    const pointFilters = [...buildingFilters]
    if ( pointidFilter ) {
      pointFilters.push(pointidFilter)
    }
    let pointQueryError = null
    let pointQueryPromise = this.postProcessingService.findCorrelationSummaryByPointRecords(
      {...queryReq,filters:pointFilters}
    ).then(resp=>{
      this.pointRecs = resp.rows
    }).catch(error=>{
      pointQueryError=error;
      this.postProcessingService.publishError(error,this)
    })

    await Promise.allSettled([buildingQueryPromise,pathQueryPromise,pointQueryPromise])
    
    const error = buildingQueryError || pathQueryError || pointQueryError
    if ( error ) {
      throw error
    }

    this.participants = []
    const oldBuildingIds = this.buildingIds
    this.buildingIds = []
    this.stages = []
    for( const rec of [...this.buildingRecs, ...this.pointRecs] ) {        
      if ( this.participants.indexOf(rec.participant) == -1 ) {
        this.participants.push(rec.participant)
      }
      if ( this.buildingIds.indexOf(rec.buildingid) == -1 ) {
        this.buildingIds.push(rec.buildingid)
      }
      if ( this.stages.indexOf(rec.stage) == -1 ) {
        this.stages.push(rec.stage)
      }
    }
    this.buildingIds.sort()
    this.stages.sort()
    this.participants.sort()
    if ( oldBuildingIds.length==0 || oldBuildingIds.join(',') != this.buildingIds.join(',') ) {      
      this.buildingById = {};
      try {
      this.buildings = await this.buildingService.getByIds(this.buildingIds)
      this.buildings.forEach( building => this.buildingById[building.id]=building)
      } catch ( error ) {
        console.error(error?.message||error)
        this.buildingService.publishError(error,this)
      }
    }
    // the correlation summary html template doesn't use the rows returned here because 
    // it uses buildingRecs, pathRecs, and pointRecs explicitly
    return {
      requestId: queryReq.requestId,
      pageNumber: 0,
      rows: [],
      queryRowCount: 0,
      tableRowCount: 0
    }
  }


  filterModalClosing() {
    if ( this.filtersModified() ) {
      this.load()
    }
  }

  getName = () => [this.buildingIds.join('-'),this.stages.join('-')].filter(str=>str) + '-correlation-summary'


  public exportAsCsv = async () =>  {
    this.bDownloading = true
    try {
      const recToCsvRow = ( dbViewType:string, fieldNames, rec: any ) => {        
        let fieldValues = fieldNames.map((fieldName,index) => {
          let fieldValue = rec[fieldName]
          const isDate = this.isDateColumn(this.tableColumns[index])
          if ( isDate ) {
            fieldValue = this.datePipe.transform(fieldValue,'yyyy-MM-dd HH:mm:ss.SSS','UTC')
          }
          const replacer = (key, value) => value === null ? '' : value;
          return JSON.stringify(fieldValue, replacer)
        })
        fieldValues.unshift(dbViewType)
        return fieldValues
      }

      const fieldNames = this.tableColumns.map(c=>c.name)
      const csvRows = [ 
        ...this.buildingRecs.map(rec=>recToCsvRow('building',fieldNames,rec)),
        ...this.pathRecs.map(rec=>recToCsvRow('path',fieldNames,rec)),
        ...this.pointRecs.map(rec=>recToCsvRow('point',fieldNames,rec))
      ]
      let csvStrRows = csvRows.map(csvRow=>csvRow.join(','))
      csvStrRows.unshift([ 'databaseview', fieldNames].join(','));
      let strCsv = csvStrRows.join('\r\n');
  
      var blob = new Blob([strCsv], {type: 'text/csv' })
      saveAs(blob, this.getName() + ".csv");
    } catch (error) {
      console.error(error)
      this.reportErrorFn(error)
    }
    this.bDownloading = false
  }
}
